<template>
  <BaseModalEditCreate
    mode="select"
    :is-modified="false"
    :title="$t('routes.addTraderSites')"
    :close-on-outside-click="false"
    max-width="extra"
    @save="emitSelectedRows = true"
    @dismiss-changes="emit('close')">
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr]">
        <div class="font-light">{{ $t('messages.info.ctrlClickToAddItems') }}</div>
        <div class="w-full h-full overflow-x-hidden">
          <BaseTable
            v-if="availableTraderSites"
            table-id="route-trader-sites"
            :headers="traderSitesHeaders"
            :list="availableTraderSites"
            :total-records="availableTraderSitesTotalCount"
            :emit-selected-rows="emitSelectedRows"
            only-search
            hide-multiselect-delete
            hide-multiselect-download
            @refresh-data="loadData"
            @selected-rows="onTraderSitesSelected"></BaseTable>
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import BaseTable from '../common/BaseTable.vue';
import type { TraderSite, TraderSiteWithBaseCustomer } from '@/types/Traders';
import type { TableFilterData } from '@/types/Base';
import { getRouteAvailableTraderSitesWithConditions } from '@/services/api/traders';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });
const props = defineProps<{
  routeId: string;
}>();

const emit = defineEmits(['close', 'save']);

const availableTraderSites = ref<TraderSiteWithBaseCustomer[]>([]);
const availableTraderSitesTotalCount = ref(0);
const emitSelectedRows = ref(false);

const traderSitesHeaders = [
  { value: 'traderSite.code', text: 'Site Code', type: 'text', active: false },
  { value: 'traderSite.name', text: $t('traderSites.siteName'), type: 'text', active: true },
  {
    value: 'traderSite.trader.name',
    text: $t('traderSites.traderName'),
    type: 'text',
    active: true,
  },
  { value: 'customer.code', text: $t('routes.customerCode'), type: 'text', active: true },
  {
    value: 'customer.profession',
    text: $t('routes.customerProfession'),
    type: 'text',
    active: true,
  },
  { value: 'traderSite.address', text: 'Site Address', type: 'text', active: true },
  { value: 'traderSite.isHQ', text: $t('HQ'), type: 'check', active: true },
  { value: 'traderSite.zipCode', text: $t('zipCode'), type: 'text', active: true },
];

onMounted(async () => {
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'traderSite.name',
    sortAscending: true,
    page: 1,
  });
});

async function loadData(data: TableFilterData) {
  const sortColumn =
    data.column !== undefined ? traderSitesHeaders[data.column]?.value : 'traderSite.name';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    routeId: props.routeId,
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };
  const res = await getRouteAvailableTraderSitesWithConditions(params);
  if (res) {
    availableTraderSites.value = res.data.map((item) => ({
      id: item.traderSite.id,
      ...item,
    }));
    availableTraderSitesTotalCount.value = res.count;
  }
}

function onTraderSitesSelected(items: TraderSite[]) {
  emit('save', items);
}
</script>
